import React, { useEffect, useState } from 'react';
import { Box, Grid, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AppColors, typographyClamps } from '../utils/utils';
import useToggle from '../hooks/useToggle';
import Page from '../components/Page';
import GetStartedModel from '../components/models/GetStartedModel';
import VideoModel from '../components/models/VideoModel';
import { HtmlRenderer } from '../components/HtmlRenderer';
import { LotSectionView } from '../components/LotSectionView';
import ImagePreviewDialog from '../components/models/ImagePreviewDialog';
import { ScreenZone } from './Home';

interface ScanFeature {
  title: string;
  descriptions: string;
}
const scanFeatures: ScanFeature[] = [
  {
    title: 'Lot-Dominating Format (8.5×11 or 4×6)',
    descriptions:
      'Large, colorful, and impossible to ignore — QR Buddy stickers are designed to visually dominate the vehicle window, stopping foot traffic and capturing attention instantly from across the lot.',
  },
  {
    title: 'Large Central QR Code',
    descriptions:
      'Oversized scan area allows buyers to scan directly from their vehicle — no walking, no awkward moments, no friction.',
  },
  {
    title: 'VIN & Stock Identification',
    descriptions:
      'Each QR Buddy sticker is uniquely assigned to a specific vehicle using VIN and Stock ID — making lot-wide deployment, routing, and reporting seamless.',
  },
  {
    title: 'Smart Incentive Offer Engine',
    descriptions:
      'Dynamic offer system lets dealers deploy rotating promotions fuel cards, gift cards, service credits, or seasonal incentives only rewarding customers who physically engage and test drive.',
  },
  {
    title: 'Buyer Data Capture Gateway',
    descriptions:
      'Every scan captures name, phone, and vehicle interest — instantly turning anonymous lot traffic into routed, track-able sales opportunities.',
  },
];

// ── 2-screen horizontal split (fast signup) ──
const ZONES_FAST_SIGN_UP: ScreenZone[] = [
  { label: 'Dealership In Pocket 1', image: '/static/lot_page/img_fast_signup_1.png', minX: 0.00, maxX: 0.40, minY: 0, maxY: 1 },
  { label: 'Dealership In Pocket 2', image: '/static/lot_page/img_fast_signup_2.png', minX: 0.40, maxX: 1.00, minY: 0, maxY: 1 },
];

// ── 2-screen horizontal split (schedule test drive & finace app) ──
const ZONES_SCHEDULE_TEST_DRIVE_FINANCE_APP: ScreenZone[] = [
  { label: 'Dealership In Pocket 1', image: '/static/lot_page/img_schedule_test_drives.png', minX: 0.00, maxX: 0.40, minY: 0, maxY: 1 },
  { label: 'Dealership In Pocket 2', image: '/static/lot_page/img_finance_requests.png', minX: 0.40, maxX: 1.00, minY: 0, maxY: 1 },
];


const ZONES_LOT_VEHICLE_PHOTOS: ScreenZone[] = [
  { label: 'Vehicle Photos 1', image: '/static/lot_page/screen1_vehicle_photos.png', minX: 0.00, maxX: 0.33, minY: 0, maxY: 1 },
  { label: 'Vehicle Photos 2', image: '/static/lot_page/screen2_vehicle_photos.png', minX: 0.33, maxX: 0.66, minY: 0, maxY: 1 },
  { label: 'Vehicle Photos 3', image: '/static/lot_page/screen3_vehicle_photos.png', minX: 0.66, maxX: 1.00, minY: 0, maxY: 1 },
];

const Lot: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // e.g. <600px

  // const xMargin = { xs: 2, sm: 2, md: '8%', lg: '10%' };

  const [videoUrl, setVideoUrl] = useState<string | undefined>();
  // const [isPlayIntroVideo] = useState<boolean>(false);

  const { toggle, onOpen, onClose } = useToggle();

  const {
    toggle: toggleGetStartedModel,
    // onOpen: onOpenGetStartedModel,
    onClose: onCloseGetStartedModel,
  } = useToggle();

  const [previewImagePath, setPreviewImagePath] = useState<
    string | undefined
  >();

  const {
    toggle: toggleImagePreviewModel,
    onOpen: onOpenImagePreviewModel,
    onClose: onCloseImagePreviewModel,
  } = useToggle();

  const openImageViewView = (imagePath: string | undefined) => {
    if (imagePath !== undefined) {
      setPreviewImagePath(imagePath);
      onOpenImagePreviewModel();
    }
  };

  const buildClickHandler = (zones: ScreenZone[]) =>
  (e: React.MouseEvent<HTMLImageElement>) => {
    const img  = e.currentTarget as HTMLImageElement;
    const rect = img.getBoundingClientRect();
    const rx   = (e.clientX - rect.left) / rect.width;
    const ry   = (e.clientY - rect.top)  / rect.height;

    const matched = zones.find(
      (z) => rx >= z.minX && rx < z.maxX && ry >= z.minY && ry < z.maxY
    );
    if (matched) openImageViewView(matched.image);
  };

  useEffect(() => {
    if (toggle === false) {
      setVideoUrl(undefined);
    }
  }, [toggle]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handlePlayVideo = (videoUrl: string | undefined) => {
    if (videoUrl !== undefined) {
      setVideoUrl(videoUrl);
      onOpen();
    }
  };

  return (
    <>
      <Page title={'The Lot'}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundImage: `url('/static/lot_page/img_lot_banner.png')`,
            backgroundPosition: 'top',
            backgroundSize: '100% auto',
            backgroundRepeat: 'no-repeat',
            // height: { xs: '50vh', sm: '80vh' },
            // pt: { xs: 2, sm: 4 },
          }}
        >
          <Box
            sx={{
              height: { xs: '30vh', sm: '25vh', md: '30vh', lg: '45vh' },
              width: '100%',
              position: 'relative',
            }}
          >
            {/* <Button
              variant='contained'
              sx={{
                borderRadius: 0,
                backgroundColor: AppColors.vividBlueColor2,
                color: 'white',
                textTransform: 'none',
                padding: '12px 24px',
                fontSize: typographyClamps.h6,
                whiteSpace: 'pre-line',
                lineHeight: 1.35,
                // my: { xs: 2, sm: 8 },
                '&:hover': {
                  borderRadius: 0,
                },
                position: 'absolute',
                ...(isMobile
                  ? { top: '80%', left: '50%', transform: 'translateX(-50%)' }
                  : { bottom: 0, right: '5%' }),
              }}
              onClick={() => {
                onOpenRequestDemoModel();
              }}
            >
              Request Demo
            </Button> */}
          </Box>

          <Grid
            container
            sx={{
              py: 4,
              // px: '10%',
              // mt: 4,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'top',
              // gap: 1,
              width: '100%',
            }}
          >
            <Grid size={{ xs: 12, sm: 5 }}>
              <Box
                component={'img'}
                src='/static/lot_page/img_lot_banner_overlay.png'
                sx={{
                  maxWidth: '100%',
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'start',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    bgcolor: AppColors.vividBlueColor2,
                    px: { xs: 2, sm: 4 },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: isMobile
                        ? typographyClamps.h5
                        : typographyClamps.h4,
                      color: 'white',
                    }}
                  >
                    <HtmlRenderer
                      htmlString={'<strong>Scanning</strong> on your lot'}
                    />
                  </Typography>
                </Box>

                <Box
                  component={'img'}
                  src={'/static/home_page/ic_scanning.png'}
                  height={{ xs: '70px', sm: '150px' }}
                  sx={{
                    position: 'relative',
                    right: { xs: 5, sm: 20, md: 20, lg: 10 },
                  }}
                />
              </Box>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 6.7 }}
              sx={{ mt: { xs: 0, sm: 0, md: 0, lg: 6 }, p: { xs: 1, sm: 0 } }}
              textAlign={isMobile ? 'center' : 'start'}
            >
              <Typography
                sx={{
                  fontSize: {
                    xs: typographyClamps.h5,
                    sm: typographyClamps.h5,
                    md: typographyClamps.h4,
                    lg: typographyClamps.h4,
                    xl: typographyClamps.h3,
                  },
                }}
              >
                Your Lot <strong>Shouldn’t Go Quiet</strong> At 6PM
              </Typography>

              <Typography
                sx={{
                  fontSize: typographyClamps.h5,
                  color: AppColors.grayColor,
                  mt: { xs: 2, sm: 4 },
                }}
              >
                QR Buddy keeps your vehicles working after hours — engaging
                buyers, capturing leads, and creating follow-up opportunities
                automatically.
              </Typography>

              <Stack
                direction='row'
                sx={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: { xs: 2, sm: 4 },
                  p: 4,
                }}
              >
                <Box
                  component='img'
                  src='/static/lot_page/img_lot_frustrated_buyer.png'
                  alt='Frustrated Buyer'
                  sx={{
                    flex: 1,
                    maxWidth: '50%',
                    width: '90%',
                    objectFit: 'contain',
                    mx: 'auto', // keep centered
                  }}
                />
                <Box
                  component='img'
                  src='/static/lot_page/img_lot_happy_buyer.png'
                  alt='Happy Buyer'
                  sx={{
                    flex: 1,
                    maxWidth: '50%',
                    width: '90%',
                    objectFit: 'contain',
                    mx: 'auto', // keep centered
                  }}
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            background: AppColors.aiGradient8,
          }}
        >
          <Grid
            container
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              // pt: { xs: 3, sm: '10%', md: '5%', lg: '3.5%' },
              // pb: 3,
              p: 3,
              gap: { sm: 2, md: 4 },
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Grid
              size={{ xs: 12, sm: 5.5 }}
              sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              {scanFeatures.map((item) => {
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: typographyClamps.fontSize18,
                        fontWeight: 'bold',
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: typographyClamps.fontSize16,
                        // fontSize: 'clamp(0.8rem, 1.2vw, 1rem)',
                        color: AppColors.grayColor,
                      }}
                    >
                      {item.descriptions}
                    </Typography>
                  </Box>
                );
              })}
            </Grid>

            <Grid
              size={{ xs: 12, sm: 5.5 }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                
              }}
            >
              <Typography
                sx={{
                  fontSize: typographyClamps.h5,
                  fontWeight: 'bold',
                  pl: '20%',
                  
                }}
              >
                8.5x11
              </Typography>
              <Box
                component={'img'}
                src='/static/lot_page/img_lot_qr_codes.png'
                maxHeight={{ xs: '80vh', sm: '60vh', md: '80vh', lg: '70vh' }}
                width={{ xs: '100%', sm: 'auto' }}
                sx={{ cursor: 'pointer'}}
                onClick={(e) => {
                  openImageViewView((e.currentTarget as HTMLImageElement).src);
                }}
              />
              <Typography
                sx={{
                  fontSize: typographyClamps.h5,
                  fontWeight: 'bold',
                  pl: {xs: '50%', sm: '30%'},
                }}
              >
                4x6
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            sx={{
              // mx: '10%',
              my: 4,
              pb: { xs: 2, sm: 4 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row', md: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Grid size={{ xs: 12, sm: 5.5, md: 7 }}>
              <Box sx={{ width: '100%', overflow: 'hidden' }}>
                <Box
                  component={'img'}
                  src='/static/lot_page/img_lot_woman_scanning.png'
                  sx={{
                    width: { xs: '100%', md: '100%' }, // Slightly larger to compensate for crop
                    transform: { xs: '', md: 'translateX(-10%)' },
                  }}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 5.5, md: 5 }}>
              <Stack
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  p: { xs: 2, sm: '10%' },
                }}
              >
                <Typography
                  sx={{
                    fontSize: {
                      xs: typographyClamps.h5,
                      sm: typographyClamps.h5,
                      md: typographyClamps.h4,
                    },
                    fontWeight: 'bold',
                    textAlign: 'center',
                    lineHeight: 1.2,
                  }}
                >
                  Sign up is fast...like 10 seconds fast
                </Typography>

                <Typography
                  sx={{
                    fontSize: {
                      xs: typographyClamps.h5,
                      sm: typographyClamps.h5,
                      md: typographyClamps.h4,
                    },
                    fontWeight: 'bold',
                    textAlign: 'center',
                    lineHeight: 1.2,
                    mt: { xs: 2, sm: 4 },
                  }}
                >
                  -Quick verification code
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'start',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    component={'img'}
                    src={'/static/lot_page/shield.png'}
                    height={{ xs: '70px', sm: '100px' }}
                    sx={{
                      position: 'relative',
                      left: { xs: 12, sm: 20, md: 20, lg: 25 },
                    }}
                  />
                  <Box
                    sx={{
                      bgcolor: 'white',
                      borderRadius: 20,
                      px: { xs: 2, sm: 4 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: {
                          xs: typographyClamps.h6,
                          sm: typographyClamps.body1,
                          md: typographyClamps.h5,
                        },
                        color: AppColors.vividBlueColor2,
                        '& span': {
                          color: AppColors.greenColor,
                        },
                      }}
                    >
                      <span>Verified</span> by the carrier and now compliant for
                      TCPA
                    </Typography>
                  </Box>
                </Box>
                {/* <Typography
                  sx={{
                    fontSize: {
                      xs: typographyClamps.h6,
                      sm: typographyClamps.h6,
                      md: typographyClamps.h5,
                    },
                    fontWeight: 'bold',
                    color: 'white',
                    textAlign: 'center',
                    lineHeight: 1.2,
                    mt: { xs: 1, sm: 2 },
                  }}
                >
                  Verified by the carrier and now compliant for TCPA
                </Typography> */}

                <Box
                  component={'img'}
                  src='/static/lot_page/ic_lot_clock.png'
                  sx={{
                    mt: { xs: 1, sm: 2 },
                    height: { xs: '150px', md: '250px', lg: 'auto' },
                  }}
                />
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={
            {
              // mx: xMargin,
            }
          }
        >
          <LotSectionView
            idx={0}
            label='Fast Sign-Up'
            labelIconPath={'/static/lot_page/ic_lot_fast_signup.png'}
            title=''
            subtitle=''
            features={[
              'Sign in instantly using Apple, Google, or a simple email and password — <span>no forms, no friction.</span',
              'Once completed, QR Buddy immediately <span>sends a one-time</span> verification code (OTP) to confirm a real mobile number and protect dealers from fake or low-quality leads.',
              'The entire process takes about <span>10 seconds</span> from tap to ready.',
            ]}
            footerText='Real buyers. Real phones. Instant dealership connection.'
            imagePath={'/static/lot_page/img_lot_2phones_signup.png'}
            isForFeature={true}
            openGroupImageView={buildClickHandler(ZONES_FAST_SIGN_UP)}
          />

          <LotSectionView
            idx={100}
            label='Auto Spotlight'
            labelIconPath={'/static/lot_page/ic_lot_vehicle_detail_page.png'}
            title=''
            subtitle=''
            features={[
              'Every vehicle receives its own clean, interactive profile — designed to guide buyers naturally toward meaningful action.<br/>Customers can favorite vehicles, explore full specifications, browse high-quality photos, schedule test drives, share vehicle link, and request finance options — all from one streamlined experience powered directly by your live inventory.',
              'Everything is structured for fast reading, intuitive browsing, and confident decision-making.',
            ]}
            footerText='Clear info. Simple actions. Faster buying confidence.'
            imagePath={'/static/lot_page/img_auto_spotlight_mobile.png'}
            isForFeature={true}
            openImageViewView={(_link: string, _event?: React.MouseEvent<HTMLImageElement>) => {              
              openImageViewView('/static/lot_page/img_auto_spotlight_mobile_big.png');
            }}
          />

          <LotSectionView
            idx={1}
            label='Vehicle Photos'
            labelIconPath={'/static/lot_page/ic_lot_vehicle_photos.png'}
            title=''
            subtitle=''
            features={[
              'Your photos create the emotional connection that sells the car — and QR Buddy puts them front and center.<br/>Every image is pulled directly from your inventory and displayed in a clean, high-impact gallery that lets buyers truly experience the vehicle before they ever step inside.',
              'This is where interest <span>turns into intent.</span>',
            ]}
            footerText='Clear info. Simple actions. Faster buying confidence.'
            imagePath={'/static/lot_page/img_lot_vehicle_photos_phones.png'}
            isForFeature={true}
            isSmallImage={true}
            openGroupImageView={buildClickHandler(ZONES_LOT_VEHICLE_PHOTOS)}
          />

          {/* <LotSectionView
            idx={2}
            label='Vehicle Detail Page'
            labelIconPath={'/static/lot_page/ic_lot_vehicle_detail_page.png'}
            title=''
            subtitle=''
            features={[
              'Every vehicle has its own clean, interactive profile designed to guide buyers toward action.',
              'Customers can favorite vehicles, view full specs, browse photos, schedule test drives, and request finance applications — all from one simple page powered directly by your inventory system.',
              'Everything is laid out for fast reading, easy browsing, and confident decisions.',
            ]}
            footerText='Clear info. Simple actions. Faster buying confidence.'
            imagePath={'/static/lot_page/img_lot_vehicle_detail_page_phone.png'}
            isForFeature={true}
            openImageViewView={openImageViewView}
          /> */}

          <LotSectionView
            idx={2}
            label='Vehicle Inventory'
            labelIconPath={'/static/lot_page/ic_lot_vehicle_inventory_icon.png'}
            title=''
            subtitle=''
            features={[
              'Browse your full inventory in one fast, intelligent search experience.',
              'Buyers can filter by vehicle type like SUV, truck, coupe, or minivan, use one-click filters, or ask Miles AI to instantly search and narrow options.',
              'All inventory is pulled live from your DMS or IMS for real-time accuracy.',
            ]}
            footerText='Faster searching. Better matches. More qualified buyers.'
            imagePath={'/static/lot_page/img_lot_vehicle_inventory_phone.png'}
            isForFeature={true}
            openImageViewView={openImageViewView}
          />

          <LotSectionView
            idx={3}
            label='Miles AI Agent'
            title=''
            subtitle=''
            features={[
              'Your dealership’s smartest digital team member — available 24/7.<br/>Miles understands your inventory, books test drives, sends finance applications, asks intelligent follow-up questions, and captures detailed buyer intent through natural conversation.',
              'Every interaction is recorded so your team receives high-quality, ready-to-work leads.',
            ]}
            footerText='Smarter conversations. Better data. Stronger opportunities.'
            footerIconPath={'/static/lot_page/ic_lot_miles_ai_agent_miles.png'}
            imagePath={'/static/lot_page/img_lot_miles_ai_agent_phone.png'}
            isForFeature={true}
            openImageViewView={openImageViewView}
          />

          <LotSectionView
            idx={4}
            label='Schedule Test Drives & Finance Requests'
            title=''
            subtitle=''
            features={[
              'Buyers can take control of their next step — anytime, day or night.',
              'Customers schedule test drives directly inside the app and submit finance requests through your secure application, even after hours.',
              'Miles can also explain manufacturer promotions and special offers to help guide buying decisions.',
            ]}
            footerText='24/7 booking. Faster follow-ups. Higher conversion.'
            footerIconPath={'/static/lot_page/ic_lot_schedule_test_drives.png'}
            imagePath={
              '/static/lot_page/img_lot_schedule_test_drives_phones.png'
            }
            isForFeature={true}
            isSmallImage={true}
            openGroupImageView={buildClickHandler(ZONES_SCHEDULE_TEST_DRIVE_FINANCE_APP)}
          />

          <LotSectionView
            idx={5}
            label='Engage Live With an Agent'
            title=''
            subtitle=''
            features={[
              'High-tech meets high-touch — real people, real conversations, right inside the app.',
              'Buyers can instantly message live sales reps through in-app chat while on the lot or at home, without needing texts or calls.',
              'If a rep is unavailable, Miles AI seamlessly continues the conversation.',
            ]}
            footerText='Instant connection. Faster responses. Happier buyers.'
            imagePath={'/static/lot_page/ic_lot_engage_live_phone.png'}
            isForFeature={true}
            openImageViewView={openImageViewView}
          />
        </Box>
      </Page>

      {videoUrl && (
        <VideoModel
          onClose={onClose}
          toggle={toggle}
          videoUrl={videoUrl ?? ''}
          title=''
        />
      )}

      <GetStartedModel
        onClose={onCloseGetStartedModel}
        toggle={toggleGetStartedModel}
        onSubmitSuccess={() => {}}
      />

      <ImagePreviewDialog
        open={toggleImagePreviewModel}
        onClose={onCloseImagePreviewModel}
        imageSrc={previewImagePath || ''}
        imageAlt='Image'
        title='Image Preview'
      />
    </>
  );
};

export default Lot;
